import * as React from 'react';

import { KSUID } from '@owpz/ksuid';
import { Button, cn, LspProvider } from '@rematter/ui-web';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Edge,
  Connection,
  Viewport,
  addEdge,
  ReactFlowProvider,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  ReactFlowInstance,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
  getOutgoers,
  ConnectionLineType,
  Node,
  SelectionMode,
  OnSelectionChangeParams,
} from '@xyflow/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Save } from 'lucide-react';
import '@xyflow/react/dist/style.css';

import {
  $editorNodes,
  $editorEdges,
  $editorViewport,
  $loadViewport,
  $saveViewport,
  $selectedNodeId,
  $selectedNode,
  $setEditorNodes,
  $setEditorEdges,
  $hasUnsavedChanges,
  $saveFlow,
  $editorAutomationId,
  $setSelectedNodeIds,
  $clearSelection,
} from '../../_state/automationEditor.state';

import { AutomationDetailsPanel } from './AutomationDetailsPanel';
import { FlowEditorToolbar } from './FlowEditorToolbar';
import { HelperLines } from './helpers/HelperLines';
import { useAutoLayout } from './hooks/useAutoLayout';
import { useFlowClipboard } from './hooks/useFlowClipboard';
import { useFlowHistory } from './hooks/useFlowHistory';
import { useFlowKeyboardShortcuts } from './hooks/useFlowKeyboardShortcuts';
import { useFlowValidation } from './hooks/useFlowValidation';
import { useHelperLines } from './hooks/useHelperLines';
import type { LayoutDirection } from './layout/elkLayout';
import { NodeConfigPanel } from './NodeConfigPanel';
import { NodePalette } from './NodePalette';
import {
  // Node components
  BusinessEventTriggerNode,
  HttpWebhookTriggerNode,
  ScheduleTriggerNode,
  EmailActionNode,
  HttpActionNode,
  ConditionNode,
  DelayNode,
  ApprovalNode,
  FilterNode,
  SwitchNode,
  LoopNode,
  // Node definitions
  businessEventTriggerDefinition,
  httpWebhookTriggerDefinition,
  scheduleTriggerDefinition,
  emailActionDefinition,
  httpActionDefinition,
  conditionDefinition,
  delayDefinition,
  approvalDefinition,
  filterDefinition,
  switchDefinition,
  loopDefinition,
  type BaseNodeDefinition,
} from './nodes';
import type { AutomationNode, AutomationNodeData } from './nodes/types';
import { isTriggerNodeType } from './nodes/types';
import { ValidationPanel } from './ValidationPanel';

// Stable nodeTypes object - defined at module level per React Flow best practices
// Validation counts are read directly by BaseNode from Jotai for full type safety
const nodeTypes = {
  BusinessEventTrigger: BusinessEventTriggerNode,
  HttpWebhookTrigger: HttpWebhookTriggerNode,
  ScheduleTrigger: ScheduleTriggerNode,
  EmailAction: EmailActionNode,
  HttpRequestAction: HttpActionNode,
  Condition: ConditionNode,
  Delay: DelayNode,
  Approval: ApprovalNode,
  Filter: FilterNode,
  Switch: SwitchNode,
  Loop: LoopNode,
};

// Node definitions lookup for drop handler
const nodeDefinitions: Record<string, BaseNodeDefinition> = {
  BusinessEventTrigger:
    businessEventTriggerDefinition as unknown as BaseNodeDefinition,
  HttpWebhookTrigger:
    httpWebhookTriggerDefinition as unknown as BaseNodeDefinition,
  ScheduleTrigger: scheduleTriggerDefinition as unknown as BaseNodeDefinition,
  EmailAction: emailActionDefinition as unknown as BaseNodeDefinition,
  HttpRequestAction: httpActionDefinition as unknown as BaseNodeDefinition,
  Condition: conditionDefinition as unknown as BaseNodeDefinition,
  Delay: delayDefinition as unknown as BaseNodeDefinition,
  Approval: approvalDefinition as unknown as BaseNodeDefinition,
  Filter: filterDefinition as unknown as BaseNodeDefinition,
  Switch: switchDefinition as unknown as BaseNodeDefinition,
  Loop: loopDefinition as unknown as BaseNodeDefinition,
};

const getNodeDefinition = (
  key: string | undefined,
): BaseNodeDefinition | undefined => (key ? nodeDefinitions[key] : undefined);

type FlowEditorContentProps = {
  readOnly?: boolean;
  className?: string;
};

const FlowEditorContent: React.FC<FlowEditorContentProps> = ({
  readOnly = false,
  className,
}) => {
  const [reactFlowInstance, setReactFlowInstance] =
    React.useState<ReactFlowInstance | null>(null);

  // Local buffer prevents flickering during drag by avoiding Jotai re-renders
  const [localNodes, setLocalNodes] = React.useState<AutomationNode[]>([]);
  const [localEdges, setLocalEdges] = React.useState<Edge[]>([]);

  const jotaiNodes = useAtomValue($editorNodes);
  const jotaiEdges = useAtomValue($editorEdges);
  const setEditorNodes = useSetAtom($setEditorNodes);
  const setEditorEdges = useSetAtom($setEditorEdges);
  const automationId = useAtomValue($editorAutomationId);

  const storedViewport = useAtomValue($editorViewport);
  const loadViewport = useSetAtom($loadViewport);
  const saveViewportToStorage = useSetAtom($saveViewport);

  const hasAppliedInitialViewport = React.useRef(false);

  React.useEffect(() => {
    setLocalNodes(jotaiNodes);
  }, [jotaiNodes]);

  React.useEffect(() => {
    setLocalEdges(jotaiEdges);
  }, [jotaiEdges]);

  React.useEffect(() => {
    loadViewport();
    hasAppliedInitialViewport.current = false;
  }, [automationId, loadViewport]);

  React.useEffect(() => {
    if (
      reactFlowInstance &&
      storedViewport &&
      !hasAppliedInitialViewport.current
    ) {
      reactFlowInstance.setViewport(storedViewport);
      hasAppliedInitialViewport.current = true;
    }
  }, [reactFlowInstance, storedViewport]);

  const viewportSaveTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const handleViewportChange = React.useCallback(
    (viewport: Viewport) => {
      if (viewportSaveTimeout.current) {
        clearTimeout(viewportSaveTimeout.current);
      }
      viewportSaveTimeout.current = setTimeout(() => {
        saveViewportToStorage(viewport);
      }, 500);
    },
    [saveViewportToStorage],
  );

  React.useEffect(
    () => () => {
      if (viewportSaveTimeout.current) {
        clearTimeout(viewportSaveTimeout.current);
      }
    },
    [],
  );

  const [, setSelectedNodeId] = useAtom($selectedNodeId);
  const selectedNode = useAtomValue($selectedNode);
  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);
  const saveFlow = useSetAtom($saveFlow);
  const setSelectedNodeIds = useSetAtom($setSelectedNodeIds);
  const clearSelection = useSetAtom($clearSelection);

  // Flow history (undo/redo)
  const { canUndo, canRedo, undo, redo } = useFlowHistory({
    enabled: !readOnly,
  });

  // Flow clipboard (copy/paste/cut)
  const { copy, paste, cut, hasClipboard, hasSelection } = useFlowClipboard({
    enabled: !readOnly,
  });

  // Flow validation
  const { hasErrors } = useFlowValidation({
    enabled: !readOnly,
  });

  // Helper lines for snapping
  const { helperLines, processNodesChange } = useHelperLines(!readOnly);

  // Auto layout
  const { applyLayout, isLayouting } = useAutoLayout(
    (nodes) => setEditorNodes(nodes, true),
    setEditorEdges,
  );

  const handleApplyLayout = React.useCallback(
    (direction: LayoutDirection) => {
      applyLayout({ direction });
    },
    [applyLayout],
  );

  // Delete handler for selected nodes (excluding triggers)
  const handleDeleteSelection = React.useCallback(() => {
    if (readOnly) return;

    const nodeIdsToRemove = new Set<string>();
    localNodes.forEach((node) => {
      if (node.selected && (!node.type || !isTriggerNodeType(node.type))) {
        nodeIdsToRemove.add(node.id);
      }
    });

    if (nodeIdsToRemove.size === 0) return;

    setLocalNodes((prev) => prev.filter((n) => !nodeIdsToRemove.has(n.id)));
    setEditorNodes(
      (prev) => prev.filter((n) => !nodeIdsToRemove.has(n.id)),
      true,
    );
    setLocalEdges((prev) =>
      prev.filter(
        (e) => !nodeIdsToRemove.has(e.source) && !nodeIdsToRemove.has(e.target),
      ),
    );
    setEditorEdges((prev) =>
      prev.filter(
        (e) => !nodeIdsToRemove.has(e.source) && !nodeIdsToRemove.has(e.target),
      ),
    );
    clearSelection();
  }, [readOnly, localNodes, setEditorNodes, setEditorEdges, clearSelection]);

  // Keyboard shortcuts
  useFlowKeyboardShortcuts({
    enabled: !readOnly,
    onUndo: undo,
    onRedo: redo,
    onCopy: copy,
    onPaste: paste,
    onCut: cut,
    onDelete: handleDeleteSelection,
    canUndo,
    canRedo,
    hasSelection,
  });

  const handleNodesChange: OnNodesChange = React.useCallback(
    (changes) => {
      // Apply helper lines processing if enabled
      const processedChanges = processNodesChange(
        changes,
        localNodes as AutomationNode[],
      );

      // Compute the full updated nodes state once
      const updatedNodes = applyNodeChanges(
        processedChanges,
        localNodes,
      ) as AutomationNode[];

      // Apply to local buffer immediately for smooth rendering
      setLocalNodes(updatedNodes);

      // Determine if we should sync to Jotai
      const hasDragEnd = processedChanges.some(
        (c) => c.type === 'position' && c.dragging === false,
      );
      const hasStructuralChanges = processedChanges.some(
        (c) => c.type === 'remove' || c.type === 'add' || c.type === 'replace',
      );
      const hasSelectionChanges = processedChanges.some(
        (c) => c.type === 'select',
      );

      if (hasStructuralChanges || hasDragEnd) {
        setEditorNodes(updatedNodes, true);
      } else if (hasSelectionChanges) {
        setEditorNodes(updatedNodes, false);
      }
    },
    [setEditorNodes, processNodesChange, localNodes],
  );

  const handleEdgesChange: OnEdgesChange = React.useCallback(
    (changes) => {
      setLocalEdges((currentEdges) => applyEdgeChanges(changes, currentEdges));

      const hasUserChanges = changes.some(
        (change) =>
          change.type === 'add' ||
          change.type === 'remove' ||
          change.type === 'replace',
      );

      if (hasUserChanges) {
        setEditorEdges((currentEdges) =>
          applyEdgeChanges(changes, currentEdges),
        );
      }
    },
    [setEditorEdges],
  );

  const handleConnect: OnConnect = React.useCallback(
    (params: Connection) => {
      const edgeWithId: Edge = {
        ...params,
        id: `edge_${KSUID.random()}`,
        source: params.source ?? '',
        target: params.target ?? '',
      };
      setLocalEdges((currentEdges) => addEdge(edgeWithId, currentEdges));
      setEditorEdges((currentEdges) => addEdge(edgeWithId, currentEdges));
    },
    [setEditorEdges],
  );

  const onDragOver = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = React.useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const integrationKey = event.dataTransfer.getData('integrationKey');

      if (!integrationKey || !reactFlowInstance) return;

      const definition = getNodeDefinition(integrationKey);
      if (!definition) {
        console.warn(`Unknown node type: ${integrationKey}`);
        return;
      }

      // Get the drag offset (defaults to 0,0 if not set)
      const offsetX =
        parseFloat(event.dataTransfer.getData('dragOffsetX')) || 0;
      const offsetY =
        parseFloat(event.dataTransfer.getData('dragOffsetY')) || 0;

      // Convert cursor position to flow coordinates
      const cursorPosition = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Apply offset to position node where user grabbed it
      const position = {
        x: cursorPosition.x - offsetX,
        y: cursorPosition.y - offsetY,
      };

      const newNode: AutomationNode = {
        id: `node_${KSUID.random()}`,
        type: definition.key,
        position,
        data: {
          label: definition.label,
          config: definition.defaultConfig(),
        } as AutomationNodeData,
      };

      setLocalNodes((currentNodes) => [...currentNodes, newNode]);
      setEditorNodes((currentNodes) => [...currentNodes, newNode]);
    },
    [reactFlowInstance, setEditorNodes],
  );

  const handleAddNodeFromPalette = React.useCallback(
    (definition: BaseNodeDefinition) => {
      setLocalNodes((currentNodes) => {
        const maxY = currentNodes.reduce(
          (max, n) => Math.max(max, n.position.y),
          0,
        );
        const position = { x: 250, y: maxY + 150 };

        const newNode: AutomationNode = {
          id: `node_${KSUID.random()}`,
          type: definition.key,
          position,
          data: {
            label: definition.label,
            config: definition.defaultConfig(),
          } as AutomationNodeData,
        };

        const lastNode = currentNodes[currentNodes.length - 1];
        if (lastNode) {
          const newEdge: Edge = {
            id: `edge_${KSUID.random()}`,
            source: lastNode.id,
            target: newNode.id,
            sourceHandle: null,
          };
          setLocalEdges((currentEdges) => {
            const updatedEdges = [...currentEdges, newEdge];
            setEditorEdges(updatedEdges);
            return updatedEdges;
          });
        }

        const updatedNodes = [...currentNodes, newNode];
        setEditorNodes(updatedNodes);
        return updatedNodes;
      });
    },
    [setEditorNodes, setEditorEdges],
  );

  const handleNodeClick = React.useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (!readOnly) {
        setSelectedNodeId((node as AutomationNode).id);
      }
    },
    [readOnly, setSelectedNodeId],
  );

  const handlePaneClick = React.useCallback(() => {
    setSelectedNodeId(null);
  }, [setSelectedNodeId]);

  const handleSelectionChange = React.useCallback(
    (params: OnSelectionChangeParams) => {
      if (readOnly) return;

      const selectedNodeIds = new Set(params.nodes.map((n) => n.id));
      setSelectedNodeIds(selectedNodeIds);

      // Update single selection for config panel
      if (selectedNodeIds.size === 1) {
        setSelectedNodeId([...selectedNodeIds][0]);
      } else if (selectedNodeIds.size === 0) {
        setSelectedNodeId(null);
      }
    },
    [readOnly, setSelectedNodeIds, setSelectedNodeId],
  );

  const handleSave = React.useCallback(async () => {
    if (!automationId) return;

    await saveFlow({
      id: automationId,
      nodes: localNodes,
      edges: localEdges,
    });
  }, [automationId, localNodes, localEdges, saveFlow]);

  const isValidConnection = React.useCallback(
    (connection: Edge | Connection) => {
      const target = localNodes.find((node) => node.id === connection.target);
      if (!target) return false;

      if (target.id === connection.source) return false;

      const hasCycle = (
        node: AutomationNode,
        visited = new Set<string>(),
      ): boolean => {
        if (visited.has(node.id)) return false;
        visited.add(node.id);

        for (const outgoer of getOutgoers(node, localNodes, localEdges)) {
          if (outgoer.id === connection.source) return true;
          if (hasCycle(outgoer as AutomationNode, visited)) return true;
        }
        return false;
      };

      return !hasCycle(target);
    },
    [localNodes, localEdges],
  );

  const defaultEdgeOptions = React.useMemo(
    () => ({
      type: 'smoothstep',
      animated: true,
      markerEnd: {
        type: MarkerType.Arrow,
      },
    }),
    [],
  );

  return (
    <div
      className={cn(
        '@FlowEditorContent',
        cn('@FlowEditor', 'flex h-full', className),
      )}
    >
      {!readOnly && <NodePalette onAddNode={handleAddNodeFromPalette} />}
      <div className="relative flex-1">
        {!readOnly && hasUnsavedChanges && (
          <div className="absolute top-4 left-1/2 z-10 -translate-x-1/2">
            <div className="flex items-center gap-3 rounded-lg border border-warning bg-warning/10 px-4 py-2 text-warning shadow-lg">
              <span className="text-sm font-medium">Unsaved changes</span>
              <Button
                size="small"
                variant="solid"
                color="primary"
                onPress={handleSave}
                isDisabled={hasErrors}
                icon={<Save className="size-4" />}
              >
                Save
              </Button>
            </div>
          </div>
        )}
        {!readOnly && <ValidationPanel />}
        <ReactFlow
          nodes={localNodes}
          edges={localEdges}
          onNodesChange={readOnly ? undefined : handleNodesChange}
          onEdgesChange={readOnly ? undefined : handleEdgesChange}
          onConnect={readOnly ? undefined : handleConnect}
          onDrop={readOnly ? undefined : onDrop}
          onDragOver={readOnly ? undefined : onDragOver}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          onSelectionChange={readOnly ? undefined : handleSelectionChange}
          onViewportChange={handleViewportChange}
          onInit={setReactFlowInstance}
          nodeTypes={nodeTypes}
          isValidConnection={isValidConnection}
          defaultEdgeOptions={defaultEdgeOptions}
          connectionLineType={ConnectionLineType.SmoothStep}
          defaultViewport={storedViewport ?? { x: 0, y: 0, zoom: 1 }}
          className="bg-white"
          connectOnClick={!readOnly}
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={!readOnly}
          edgesFocusable={!readOnly}
          nodesFocusable={!readOnly}
          selectionOnDrag={!readOnly}
          selectionMode={SelectionMode.Partial}
          selectionKeyCode={['Shift']}
          multiSelectionKeyCode={['Shift', 'Meta', 'Control']}
          deleteKeyCode={null}
        >
          <Background />
          <Controls />
          <MiniMap />
          {!readOnly && <HelperLines {...helperLines} />}
          {!readOnly && (
            <FlowEditorToolbar
              canUndo={canUndo}
              canRedo={canRedo}
              hasSelection={hasSelection}
              hasClipboard={hasClipboard}
              isLayouting={isLayouting}
              onUndo={undo}
              onRedo={redo}
              onCopy={copy}
              onPaste={() => paste()}
              onCut={cut}
              onLayout={handleApplyLayout}
            />
          )}
        </ReactFlow>
      </div>
      {selectedNode ? (
        !readOnly && (
          <NodeConfigPanel key={selectedNode.id} node={selectedNode} />
        )
      ) : (
        <AutomationDetailsPanel />
      )}
    </div>
  );
};

export const FlowEditor: React.FC<FlowEditorContentProps> = (props) => (
  <ReactFlowProvider>
    <LspProvider>
      <FlowEditorContent {...props} />
    </LspProvider>
  </ReactFlowProvider>
);
